import { useEffect, useState } from 'react'
import { motion } from 'framer-motion'
import { studentsApi, type BirthdayReminderResponse } from '@/api/endpoints/students'
import Spinner from '@/components/common/Spinner'

interface Props {
  classId?: string
  title?: string
}

export default function BirthdayReminders({ classId, title = 'Birthday Reminders (Next 2 Weeks)' }: Props) {
  const [reminders, setReminders] = useState<BirthdayReminderResponse[]>([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    let mounted = true
    const load = async () => {
      setLoading(true)
      try {
        const data = await studentsApi.getBirthdayReminders(classId, 14)
        if (mounted) setReminders(data)
      } catch {
        if (mounted) setReminders([])
      } finally {
        if (mounted) setLoading(false)
      }
    }
    load()
    return () => { mounted = false }
  }, [classId])

  if (loading) {
    return <Spinner label="Loading birthday reminders..." />
  }

  if (reminders.length === 0) {
    return null
  }

  return (
    <div className="mt-4 p-4 rounded-lg border border-gray-200 bg-gray-50">
      <h3 className="text-sm font-semibold mb-2">{title}</h3>
      <div className="space-y-2">
        {reminders.map((reminder) => {
          const birthdayDate = new Date(reminder.birthday_date)
          const dayOfWeek = birthdayDate.toLocaleDateString('en-US', { weekday: 'long' })
          const monthDay = birthdayDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })

          return (
            <motion.div
              key={reminder.student_id}
              initial={{ opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
              className="text-sm"
            >
              <div>
                <span className="font-medium">Happy Birthday to {reminder.student_name}</span>
                {' '}
                <span className="text-gray-600">
                  on {dayOfWeek} {monthDay}
                  {reminder.days_until_birthday > 0 && ` (in ${reminder.days_until_birthday} day${reminder.days_until_birthday !== 1 ? 's' : ''})`}
                </span>
              </div>
              {reminder.allergen_alerts && reminder.allergen_alerts.length > 0 && (
                <div className="mt-1 text-xs font-bold text-red-600">
                  ⚠️ Allergen notice: {reminder.allergen_alerts.join(', ')}
                </div>
              )}
            </motion.div>
          )
        })}
      </div>
    </div>
  )
}

